// ============================================
// Home Care Compliance Risk Checklist v1.0
// State-specific items across 7 categories
// Number of items varies by state requirements
// ============================================

import type { ChecklistCategory, ChecklistItemDefinition, ScoringConfig } from '../types';
import {
  getChecklistForState as getStateChecklist,
  isValidStateCode,
  hasDiagnosticFile
} from './states';

// ============================================
// STATE-SPECIFIC CHECKLIST FUNCTIONS
// ============================================

// Get checklist for a specific state (uses diagnostic questions for all 51 states)
export function getChecklistForState(stateCode: string): ChecklistCategory[] {
  const code = stateCode.toUpperCase();
  if (isValidStateCode(code)) {
    return getStateChecklist(code);  // Uses diagnostic questions!
  }
  // Return generic checklist for unknown state codes
  return COMPLIANCE_CHECKLIST;
}

// All states have specific diagnostic-based checklists now
export function hasStateSpecificChecklist(stateCode: string): boolean {
  return hasDiagnosticFile(stateCode);
}

// Get total items for a state
export function getTotalItemsForState(stateCode: string): number {
  const checklist = getChecklistForState(stateCode);
  return checklist.reduce((sum, cat) => sum + cat.items.length, 0);
}

// Re-export state options for dropdowns
export { getStateOptions, requiresStateLicense } from './states';

// ============================================
// SCORING CONFIGURATION
// ============================================

export const SCORING_CONFIG: ScoringConfig = {
  compliantPoints: 0,
  atRiskPoints: 2,
  nonCompliantPoints: 5,
  unknownPoints: 3,
  severityWeights: {
    low: 1,
    medium: 2,
    high: 3,
  },
  bands: {
    low: { min: 0, max: 24 },
    medium: { min: 25, max: 74 },
    high: { min: 75, max: null },
  },
};

// ============================================
// CATEGORY DEFINITIONS
// ============================================

export const COMPLIANCE_CHECKLIST: ChecklistCategory[] = [
  // =========================================
  // A. Licensing & Operations (10 items)
  // =========================================
  {
    id: 'licensing-operations',
    name: 'A. Licensing & Operations',
    description: 'Agency licensing, insurance, and operational policies',
    order: 1,
    items: [
      {
        id: 'A1',
        text: 'Agency license is current and active',
        helpText: 'State provider license must be valid and not expired',
        regulatoryReference: '12VAC30-120-370',
        severity: 'high',
        category: 'licensing-operations',
        order: 1,
        complianceGuidance: 'Verify license expiration date and renewal status',
        remediationSteps: ['Check license expiration date', 'Submit renewal 90 days before expiration', 'Ensure all fees are paid'],
      },
      {
        id: 'A2',
        text: 'License is available for inspection on premises',
        helpText: 'Physical or digital copy must be readily accessible',
        regulatoryReference: '12VAC30-120-370',
        severity: 'medium',
        category: 'licensing-operations',
        order: 2,
      },
      {
        id: 'A3',
        text: 'Ownership and administrator information is current with DMAS',
        helpText: 'All changes must be reported within 30 days',
        regulatoryReference: '12VAC30-120-380',
        severity: 'medium',
        category: 'licensing-operations',
        order: 3,
      },
      {
        id: 'A4',
        text: 'Written policies and procedures are current and complete',
        helpText: 'Must cover all required operational areas per DMAS requirements',
        severity: 'medium',
        category: 'licensing-operations',
        order: 4,
      },
      {
        id: 'A5',
        text: 'Client service agreements are current and signed',
        helpText: 'Agreements must include all required elements per state regulations',
        severity: 'medium',
        category: 'licensing-operations',
        order: 5,
      },
      {
        id: 'A6',
        text: 'Incident reporting process is documented and followed',
        helpText: 'Critical incidents must be reported within 24 hours',
        severity: 'high',
        category: 'licensing-operations',
        order: 6,
      },
      {
        id: 'A7',
        text: 'Emergency preparedness plan is documented',
        helpText: 'Must include procedures for natural disasters, pandemics, and other emergencies',
        severity: 'medium',
        category: 'licensing-operations',
        order: 7,
      },
      {
        id: 'A8',
        text: 'Complaint and grievance handling process is in place',
        helpText: 'Must document all complaints and resolution actions',
        severity: 'low',
        category: 'licensing-operations',
        order: 8,
      },
      {
        id: 'A9',
        text: 'Quality assurance reviews are documented',
        helpText: 'Regular QA reviews should be conducted and documented',
        severity: 'medium',
        category: 'licensing-operations',
        order: 9,
      },
      {
        id: 'A10',
        text: 'Record retention policy meets state requirements',
        helpText: 'Records must be retained for minimum of 6 years',
        severity: 'medium',
        category: 'licensing-operations',
        order: 10,
      },
    ],
  },

  // =========================================
  // B. Personnel Files (10 items)
  // =========================================
  {
    id: 'personnel-files',
    name: 'B. Personnel Files',
    description: 'Employee documentation and personnel record requirements',
    order: 2,
    items: [
      {
        id: 'B1',
        text: 'Personnel file maintained for each caregiver',
        helpText: 'Centralized file with all required documents',
        severity: 'high',
        category: 'personnel-files',
        order: 1,
      },
      {
        id: 'B2',
        text: 'Government-issued photo ID on file',
        helpText: 'Valid driver\'s license, passport, or state ID',
        severity: 'medium',
        category: 'personnel-files',
        order: 2,
      },
      {
        id: 'B3',
        text: 'Signed job description on file',
        helpText: 'Must outline duties, responsibilities, and qualifications',
        severity: 'medium',
        category: 'personnel-files',
        order: 3,
      },
      {
        id: 'B4',
        text: 'HIPAA/confidentiality agreement signed',
        helpText: 'Employee acknowledgment of privacy obligations',
        severity: 'high',
        category: 'personnel-files',
        order: 4,
      },
      {
        id: 'B5',
        text: 'Code of conduct/ethics agreement signed',
        helpText: 'Written acknowledgment of behavioral expectations',
        severity: 'medium',
        category: 'personnel-files',
        order: 5,
      },
      {
        id: 'B6',
        text: 'Attendance policy acknowledgment signed',
        helpText: 'Employee understands attendance requirements',
        severity: 'low',
        category: 'personnel-files',
        order: 6,
      },
      {
        id: 'B7',
        text: 'Training policy acknowledgment signed',
        helpText: 'Employee understands ongoing training requirements',
        severity: 'low',
        category: 'personnel-files',
        order: 7,
      },
      {
        id: 'B8',
        text: 'Background check consent form signed',
        helpText: 'Written authorization for background screening',
        severity: 'high',
        category: 'personnel-files',
        order: 8,
      },
      {
        id: 'B9',
        text: 'I-9 Employment Eligibility Verification completed',
        helpText: 'Federal requirement for all employees',
        regulatoryReference: '8 CFR 274a.2',
        severity: 'high',
        category: 'personnel-files',
        order: 9,
      },
      {
        id: 'B10',
        text: 'Tax onboarding forms (W-4, state withholding) completed',
        helpText: 'Required for payroll processing',
        severity: 'medium',
        category: 'personnel-files',
        order: 10,
      },
    ],
  },

  // =========================================
  // C. Background Screening (5 items)
  // =========================================
  {
    id: 'background-screening',
    name: 'C. Background Screening',
    description: 'Criminal background checks and screening requirements',
    order: 3,
    items: [
      {
        id: 'C1',
        text: 'Criminal background check completed before employment',
        helpText: 'State Police and/or FBI fingerprint background check',
        regulatoryReference: '12VAC30-120-400',
        severity: 'high',
        category: 'background-screening',
        order: 1,
      },
      {
        id: 'C2',
        text: 'Background check results documented in file',
        helpText: 'Copy of results or verification letter on file',
        severity: 'high',
        category: 'background-screening',
        order: 2,
      },
      {
        id: 'C3',
        text: 'OIG/GSA exclusion list checks completed',
        helpText: 'Verify employee is not excluded from federal programs',
        severity: 'high',
        category: 'background-screening',
        order: 3,
      },
      {
        id: 'C4',
        text: 'Re-screening policy in place per state requirements',
        helpText: 'Background checks renewed at required intervals',
        severity: 'high',
        category: 'background-screening',
        order: 4,
      },
      {
        id: 'C5',
        text: 'Driving record check for staff who transport clients',
        helpText: 'Only applicable if employees drive clients',
        severity: 'medium',
        category: 'background-screening',
        order: 5,
      },
    ],
  },

  // =========================================
  // D. Health & Safety (7 items)
  // =========================================
  {
    id: 'health-safety',
    name: 'D. Health & Safety',
    description: 'Employee health screenings and safety training',
    order: 4,
    items: [
      {
        id: 'D1',
        text: 'TB screening is current for all direct care staff',
        helpText: 'Initial screening plus annual assessment',
        regulatoryReference: '12VAC30-120-410',
        severity: 'high',
        category: 'health-safety',
        order: 1,
      },
      {
        id: 'D2',
        text: 'Health clearance/physical exam documented',
        helpText: 'Fitness for duty assessment',
        severity: 'medium',
        category: 'health-safety',
        order: 2,
      },
      {
        id: 'D3',
        text: 'CPR certification current',
        helpText: 'Must be current and from approved provider',
        severity: 'high',
        category: 'health-safety',
        order: 3,
      },
      {
        id: 'D4',
        text: 'First Aid certification current',
        helpText: 'Must be current and from approved provider',
        severity: 'high',
        category: 'health-safety',
        order: 4,
      },
      {
        id: 'D5',
        text: 'Infection control training completed',
        helpText: 'Initial and ongoing training on infection prevention',
        severity: 'medium',
        category: 'health-safety',
        order: 5,
      },
      {
        id: 'D6',
        text: 'Bloodborne pathogens training completed',
        helpText: 'OSHA required training for healthcare workers',
        regulatoryReference: '29 CFR 1910.1030',
        severity: 'medium',
        category: 'health-safety',
        order: 6,
      },
      {
        id: 'D7',
        text: 'PPE policy acknowledged',
        helpText: 'Employee understands proper use of personal protective equipment',
        severity: 'medium',
        category: 'health-safety',
        order: 7,
      },
    ],
  },

  // =========================================
  // E. Training & Competency (8 items)
  // =========================================
  {
    id: 'training-competency',
    name: 'E. Training & Competency',
    description: 'Staff training, orientation, and competency verification',
    order: 5,
    items: [
      {
        id: 'E1',
        text: 'New hire orientation program completed',
        helpText: 'Comprehensive orientation before providing care',
        severity: 'high',
        category: 'training-competency',
        order: 1,
      },
      {
        id: 'E2',
        text: 'Competency evaluation completed and documented',
        helpText: 'Skills assessment for direct care tasks',
        severity: 'medium',
        category: 'training-competency',
        order: 2,
      },
      {
        id: 'E3',
        text: 'Ongoing training hours tracked (12 hours/year minimum)',
        helpText: 'Annual in-service training requirements',
        regulatoryReference: '12VAC30-120-420',
        severity: 'high',
        category: 'training-competency',
        order: 3,
      },
      {
        id: 'E4',
        text: 'Dementia/Alzheimer\'s training completed (if applicable)',
        helpText: 'Required for staff serving clients with cognitive impairment',
        severity: 'medium',
        category: 'training-competency',
        order: 4,
      },
      {
        id: 'E5',
        text: 'Transfer and mobility assistance training completed',
        helpText: 'Safe lifting and transfer techniques',
        severity: 'medium',
        category: 'training-competency',
        order: 5,
      },
      {
        id: 'E6',
        text: 'Medication assistance training completed (if applicable)',
        helpText: 'Required for staff assisting with medications',
        severity: 'high',
        category: 'training-competency',
        order: 6,
      },
      {
        id: 'E7',
        text: 'Abuse/neglect recognition and reporting training completed',
        helpText: 'Mandatory reporter training',
        severity: 'high',
        category: 'training-competency',
        order: 7,
      },
      {
        id: 'E8',
        text: 'Client rights training completed',
        helpText: 'Understanding of client rights and dignity',
        severity: 'medium',
        category: 'training-competency',
        order: 8,
      },
    ],
  },

  // =========================================
  // F. Care Delivery (5 items)
  // =========================================
  {
    id: 'care-delivery',
    name: 'F. Care Delivery',
    description: 'Care plan management and service documentation',
    order: 6,
    items: [
      {
        id: 'F1',
        text: 'Care plans completed before first shift',
        helpText: 'Individualized care plan for each client',
        severity: 'high',
        category: 'care-delivery',
        order: 1,
      },
      {
        id: 'F2',
        text: 'Caregiver acknowledgment of care plan on file',
        helpText: 'Staff signs that they have reviewed and understand the care plan',
        severity: 'medium',
        category: 'care-delivery',
        order: 2,
      },
      {
        id: 'F3',
        text: 'Electronic Visit Verification (EVV) method in place',
        helpText: 'System to verify visit times and locations',
        regulatoryReference: '21st Century Cures Act',
        severity: 'high',
        category: 'care-delivery',
        order: 3,
      },
      {
        id: 'F4',
        text: 'Missed visit reporting and follow-up process documented',
        helpText: 'Protocol for handling missed or late visits',
        severity: 'medium',
        category: 'care-delivery',
        order: 4,
      },
      {
        id: 'F5',
        text: 'Supervisory visits and follow-ups documented',
        helpText: 'Regular supervisor check-ins with clients and staff',
        severity: 'medium',
        category: 'care-delivery',
        order: 5,
      },
    ],
  },

  // =========================================
  // G. Documentation Integrity (5 items)
  // =========================================
  {
    id: 'documentation-integrity',
    name: 'G. Documentation Integrity',
    description: 'Record keeping, document control, and audit readiness',
    order: 7,
    items: [
      {
        id: 'G1',
        text: 'Document expiration tracking system in place',
        helpText: 'System to track license, certification, and clearance expiration dates',
        severity: 'high',
        category: 'documentation-integrity',
        order: 1,
      },
      {
        id: 'G2',
        text: 'Expiration reminder notifications active',
        helpText: 'Automated or manual reminders sent before documents expire',
        severity: 'medium',
        category: 'documentation-integrity',
        order: 2,
      },
      {
        id: 'G3',
        text: 'Audit-ready folder structure maintained',
        helpText: 'Personnel files organized for easy review',
        severity: 'medium',
        category: 'documentation-integrity',
        order: 3,
      },
      {
        id: 'G4',
        text: 'Version control and change log for policies',
        helpText: 'Track updates and changes to policies and procedures',
        severity: 'low',
        category: 'documentation-integrity',
        order: 4,
      },
      {
        id: 'G5',
        text: 'Access controls in place for sensitive records',
        helpText: 'Only authorized personnel can access personnel and client files',
        severity: 'medium',
        category: 'documentation-integrity',
        order: 5,
      },
    ],
  },
];

// ============================================
// HELPER FUNCTIONS
// ============================================

// Note: Item counts vary by state (typically 20-50 items based on state-specific diagnostics)

export function getAllChecklistItems(): ChecklistItemDefinition[] {
  return COMPLIANCE_CHECKLIST.flatMap(category => category.items);
}

export function getChecklistItemById(itemId: string): ChecklistItemDefinition | undefined {
  for (const category of COMPLIANCE_CHECKLIST) {
    const item = category.items.find(i => i.id === itemId);
    if (item) return item;
  }
  return undefined;
}

export function getCategoryById(categoryId: string): ChecklistCategory | undefined {
  return COMPLIANCE_CHECKLIST.find(c => c.id === categoryId);
}

export function calculateMaxPossibleScore(): number {
  let maxScore = 0;
  for (const category of COMPLIANCE_CHECKLIST) {
    for (const item of category.items) {
      const weight = SCORING_CONFIG.severityWeights[item.severity];
      maxScore += SCORING_CONFIG.nonCompliantPoints * weight;
    }
  }
  return maxScore;
}

export const MAX_POSSIBLE_SCORE = calculateMaxPossibleScore();

// ============================================
// ONBOARDING RISK MAPPING
// ============================================

export type OnboardingRisk = 'low' | 'medium' | 'high';

export function calculateOnboardingRisk(
  method: 'digital' | 'mixed' | 'manual',
  avgDays: number
): OnboardingRisk {
  if (method === 'digital') {
    if (avgDays <= 3) return 'low';
    if (avgDays <= 7) return 'medium';
    return 'high';
  }

  if (method === 'mixed') {
    if (avgDays <= 4) return 'low';
    if (avgDays <= 7) return 'medium';
    if (avgDays <= 14) return 'medium'; // medium-high in spec, use medium
    return 'high';
  }

  // Manual
  if (avgDays <= 7) return 'medium';
  return 'high';
}
